import { useMemo } from "react"
import { useParams } from "react-router-dom"
import { Box, CircularProgress, Container, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"
import type { IMyOrderDetails, IOrderDeliveryLocation } from "../../../../payments/orderTypes"
import {
    getOrderDetails,
    getPublicOrderDetails,
    OrderDetailsResponse,
    OrdersQuery,
} from "../../../../receipt/ordersApi"
import { PaymentErrorType, PaymentStatus, ProgressState } from "../../settings/wallets/status/types/paymentTypes"
import { usePaymentFlow } from "../../settings/wallets/status/hooks/paymentHooks"
import { useEffectOnce } from "../../../../../shared/hooks/useEffectOnce"

import { TakeawayOrderConfirmed } from "../components/TakeawayOrderConfirmed"
import { TakeawayOrderFailed } from "../components/TakeawayOrderFailed"

const useStyles = makeStyles()((theme) => ({
    container: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(3),
    },
    content: {
        padding: theme.spacing(6),
        maxWidth: "25rem",
        width: "100%",
        textAlign: "center",
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    description: {
        color: theme.palette.text.secondary,
        lineHeight: 1.6,
        marginBottom: theme.spacing(4),
    },
    loaderContainer: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: theme.spacing(2),
    },
    countDown: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
    },
}))

function buildChildOrders(
    masterOrder: IMyOrderDetails,
    deliveryLocation: IOrderDeliveryLocation | undefined,
): IMyOrderDetails[] {
    if (!masterOrder.deliveries?.length) return [masterOrder]

    return masterOrder.deliveries.map((delivery) => ({
        ...masterOrder,
        id: delivery.orderId ?? masterOrder.id,
        uid: delivery.orderUid ?? masterOrder.uid,
        deliveries: [{ ...delivery, deliveryLocation: delivery.deliveryLocation ?? deliveryLocation }],
        receipt: delivery.receipt,
    }))
}

type FailedViewProps = Readonly<{
    isPublic: boolean
}>

function PaymentFailedView({ isPublic }: FailedViewProps) {
    return (
        <LocalizedStrict id="takeaway-payment-failed" attrs={{ title: true, description: true }}>
            <TakeawayOrderFailed
                title="Payment Failed"
                description="Payment could not be completed due to an unknown error. Please try again later."
                isPublic={isPublic}
            />
        </LocalizedStrict>
    )
}

function PaymentTimeoutView({ isPublic }: FailedViewProps) {
    return (
        <LocalizedStrict id="takeaway-payment-timeout" attrs={{ title: true, description: true }}>
            <TakeawayOrderFailed
                title="Payment Timed Out"
                description="We haven't received confirmation from your payment provider. Please check your netbank to be sure that the payment was completed."
                isPublic={isPublic}
            />
        </LocalizedStrict>
    )
}

type PaymentProcessingViewProps = Readonly<{
    progressState: ProgressState | undefined
}>

function PaymentProcessingView({ progressState }: PaymentProcessingViewProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    return (
        <Container className={classes.container}>
            <Box className={classes.content}>
                <LocalizedStrict id="takeaway-payment-poll-title">
                    <Typography variant="h5" className={classes.title}>
                        Processing Payment
                    </Typography>
                </LocalizedStrict>

                {progressState?.remaining && (
                    <Box className={classes.loaderContainer}>
                        <CircularProgress size="6.25rem" thickness={4} />
                        <Box className={classes.countDown}>
                            <Typography>{dateTimeFactory.formatDuration(progressState.remaining)}</Typography>
                        </Box>
                    </Box>
                )}

                <LocalizedStrict id="takeaway-payment-poll-description">
                    <Typography variant="body1" className={classes.description}>
                        Please wait while we process your payment. This may take a few moments.
                    </Typography>
                </LocalizedStrict>
            </Box>
        </Container>
    )
}

type TakeawayPaymentPollProps = Readonly<{
    isPublic?: boolean
}>

export function TakeawayPaymentPoll({ isPublic = false }: TakeawayPaymentPollProps = {}) {
    const { orderUid } = useParams<{ orderUid: string }>()

    const { progressState, paymentState, pollForPaymentState } = usePaymentFlow({ isPublic })

    useEffectOnce(() => {
        if (orderUid) {
            pollForPaymentState(orderUid)
        }
    })

    const masterOrder = paymentState.status === PaymentStatus.COMPLETED ? paymentState.response.order : undefined
    const subOrderId = masterOrder?.deliveries?.[0]?.orderId
    const subOrderUid = masterOrder?.deliveries?.[0]?.orderUid

    const selectSubDeliveryLocation = (data: OrderDetailsResponse) => data.orders[0]?.deliveries?.[0]?.deliveryLocation

    const { data: subDeliveryLocationAuth } = useApiQuery(getOrderDetails, {
        queryName: OrdersQuery.ORDER_DETAILS,
        dependencies: { orderId: subOrderId ?? 0 },
        enabled: !isPublic && Boolean(subOrderId),
        select: selectSubDeliveryLocation,
    })

    const { data: subDeliveryLocationPublic } = useApiQuery(getPublicOrderDetails, {
        queryName: OrdersQuery.ORDER_DETAILS_PUBLIC,
        dependencies: { orderUid: subOrderUid ?? "" },
        enabled: isPublic && Boolean(subOrderUid),
        select: selectSubDeliveryLocation,
    })

    const subDeliveryLocation = subDeliveryLocationAuth ?? subDeliveryLocationPublic

    const childOrders = useMemo(
        () => (masterOrder ? buildChildOrders(masterOrder, subDeliveryLocation) : undefined),
        [masterOrder, subDeliveryLocation],
    )

    if (!orderUid) {
        return <PaymentFailedView isPublic={isPublic} />
    }

    if (paymentState.status === PaymentStatus.COMPLETED) {
        return <TakeawayOrderConfirmed orders={childOrders} totalPrice={masterOrder?.price} isPublic={isPublic} />
    }

    if (paymentState.status === PaymentStatus.FAILED) {
        const isTimeout = paymentState.errorType === PaymentErrorType.TIMEOUT
        return isTimeout ? <PaymentTimeoutView isPublic={isPublic} /> : <PaymentFailedView isPublic={isPublic} />
    }

    return <PaymentProcessingView progressState={progressState} />
}
